import React, { useEffect, useRef, useState } from "react";
import { consultarEntradaEscaner, consumirEntradaEscaner } from '../../utils/reservasPrivadas';
import { Html5Qrcode } from "html5-qrcode";
import { useNavigate } from "react-router-dom";

import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../../firebase/firebaseConfig";
import { doc } from 'firebase/firestore';
import { getDoc } from '../../utils/firestoreRead';

import "../invitaciones/escaner.css";

const allowedRoles = ["owner", "socio", "organizador", "porteria"];

// ✅ tiempo mínimo de “pantalla resultado”
const AUTO_BACK_MS = 2000;

// ✅ mini delay para iOS al reiniciar cámara
const IOS_RESTART_DELAY_MS = 450;

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

function parseEntradaId(raw) {
  try {
    const t = (raw || "").toString().trim();
    if (!t) return null;

    if (t.startsWith("{")) {
      const obj = JSON.parse(t);
      return obj.entradaId || obj.id || null;
    }

    if (/^https?:\/\//i.test(t)) {
      const url = new URL(t);
      return (
        url.searchParams.get("entradaId") ||
        url.searchParams.get("id") ||
        url.pathname.split("/").filter(Boolean).pop() ||
        null
      );
    }

    if (t.includes("/entrada/")) {
      return t.split("/entrada/")[1].split(/[?#]/)[0];
    }

    return t;
  } catch {
    return (raw || "").toString().trim();
  }
}

export default function EscanearEntradas() {
  const navigate = useNavigate();

  const [allowed, setAllowed] = useState(false);
  const [msg, setMsg] = useState("");

  const [scannerReady, setScannerReady] = useState(false);
  const [error, setError] = useState(null);

  const [entrada, setEntrada] = useState(null);
  const [entradaId, setEntradaId] = useState(null);

  const [isMarking, setIsMarking] = useState(false);

  const qrRef = useRef(null);
  const startingRef = useRef(false);
  const mountedRef = useRef(true);
  const backTimerRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (backTimerRef.current) clearTimeout(backTimerRef.current);
    };
  }, []);

  // ✅ Verifica rol (igual que invitaciones)
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        setMsg("Debes iniciar sesión para usar el escáner.");
        navigate("/", { replace: true });
        return;
      }

      try {
        const snap = await getDoc(doc(db, "usuarios", u.uid));
        const rol = snap.exists() ? snap.data().rol : null;

        if (!rol || !allowedRoles.includes(rol)) {
          setMsg("No tienes permisos para usar el escáner.");
          navigate("/sin-acceso", { replace: true });
          return;
        }

        setAllowed(true);
      } catch {
        setMsg("No se pudo verificar permisos.");
        navigate("/", { replace: true });
      }
    });

    return () => unsub();
  }, [navigate]);

  const cleanupDomDuplicates = () => {
    const host = document.getElementById("qr-reader");
    if (!host) return;

    const videos = Array.from(host.querySelectorAll("video"));
    if (videos.length > 1) videos.slice(1).forEach((v) => v.remove());

    const canvases = Array.from(host.querySelectorAll("canvas"));
    canvases.forEach((c) => {
      c.style.display = "none";
      c.setAttribute("aria-hidden", "true");
    });

    const firstVideo = host.querySelector("video");
    if (firstVideo) {
      firstVideo.style.width = "100%";
      firstVideo.style.height = "100%";
      firstVideo.style.objectFit = "cover";
      firstVideo.style.borderRadius = "14px";
      firstVideo.style.display = "block";
    }
  };

  const stopAndClear = async () => {
    try {
      if (qrRef.current) {
        try {
          await qrRef.current.stop();
        } catch {}
        try {
          await qrRef.current.clear();
        } catch {}
      }
    } finally {
      qrRef.current = null;
      if (mountedRef.current) setScannerReady(false);
    }
  };

  const internalStart = async () => {
    const host = document.getElementById("qr-reader");
    if (!host) return;

    host.innerHTML = "";
    await stopAndClear();

    const qr = new Html5Qrcode("qr-reader");
    qrRef.current = qr;

    const onScanSuccess = async (decodedText) => {
      try {
        const id = parseEntradaId(decodedText);
        if (!id) {
          setError("QR no reconocido");
          return;
        }

        const normalized = await consultarEntradaEscaner(id);

        setEntradaId(id);
        setEntrada(normalized);

        // parar cámara al encontrar
        await stopAndClear();
      } catch (e) {
        console.error(e);
        setError("Error al procesar el QR");
      }
    };

    await qr.start(
      { facingMode: "environment" },
      { fps: 10, qrbox: { width: 280, height: 280 }, aspectRatio: 1 },
      onScanSuccess,
      () => {}
    );

    if (mountedRef.current) setScannerReady(true);

    setTimeout(cleanupDomDuplicates, 50);
    setTimeout(cleanupDomDuplicates, 250);
    setTimeout(cleanupDomDuplicates, 600);
  };

  // ✅ Start con retry (iOS)
  const startCamera = async () => {
    if (!allowed) return;
    if (startingRef.current) return;
    startingRef.current = true;

    try {
      setError(null);
      // iOS: espera un poco antes de encender de nuevo
      await sleep(IOS_RESTART_DELAY_MS);

      try {
        await internalStart();
      } catch (e1) {
        console.warn("Start camera failed, retrying…", e1);
        // retry 1 vez
        await sleep(650);
        await internalStart();
      }
    } catch (e) {
      console.error(e);
      if (mountedRef.current) {
        setError("No se pudo iniciar la cámara. Revisa permisos.");
        setScannerReady(false);
      }
    } finally {
      startingRef.current = false;
    }
  };

  useEffect(() => {
    if (!allowed) return;
    startCamera();
    return () => {
      stopAndClear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed]);

  const escanearOtra = async () => {
    if (backTimerRef.current) clearTimeout(backTimerRef.current);
    setEntrada(null);
    setEntradaId(null);
    setError(null);
    setIsMarking(false);

    // ✅ asegura que quedó todo parado antes de reiniciar
    await stopAndClear();
    await startCamera();
  };

  const esValida = entrada?.estado === 'no_usada';
  const estadoTexto = { no_usada: 'NO USADA', usada: 'USADA', anulada: 'ANULADA', bloqueada: 'BLOQUEADA' }[entrada?.estado] || 'NO VÁLIDA';

  // ✅ MISMA LÓGICA: marcar usada y volver SOLO al escáner con delay >= 2s
  const marcarUsada = async () => {
    if (!entradaId || entrada?.estado !== 'no_usada' || isMarking) return;

    setIsMarking(true);
    try {
      const usada = await consumirEntradaEscaner(entradaId);
      setEntrada(usada);

      // ✅ mínimo 2 segundos antes de volver
      backTimerRef.current = setTimeout(() => {
        escanearOtra();
      }, AUTO_BACK_MS);
    } catch (e) {
      console.error(e);
      alert("Error al marcar como usada");
      setIsMarking(false);
    }
  };

  const toggleFullscreen = async () => {
    const el = document.documentElement;
    try {
      if (!document.fullscreenElement) await el.requestFullscreen?.();
      else await document.exitFullscreen?.();
    } catch {}
  };

  if (!allowed) {
    return (
      <div className="scan-layout">
        <div className="scan-card" style={{ marginTop: 40 }}>
          <div className="scan-title">Acceso</div>
          <p className="scan-msg">{msg || "Verificando permisos…"}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="scan-layout">
      <header className="scan-header">
        <div className="brand-dot" />
        <span>Palmarama · Escáner de Entradas</span>

        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button className="btn ghost" onClick={toggleFullscreen}>
            Pantalla completa
          </button>
        </div>
      </header>

      <section className="scan-camera">
        <div className="scan-instructions">
          <h2>Escanear Entrada</h2>
          <p>
            {scannerReady ? "Apunta y centra el QR en el marco." : "Iniciando cámara…"}
          </p>
        </div>

        {!entrada && (
          <div className="qr-reader-surface">
            <div className="qr-box">
              <div id="qr-reader" />
            </div>

            <div className="qr-frame">
              <span className="corner tl" />
              <span className="corner tr" />
              <span className="corner bl" />
              <span className="corner br" />
            </div>
          </div>
        )}

        <div className="scan-tips">
          <ul>
            <li>Si está oscuro, sube el brillo.</li>
            <li>Evita reflejos y mantén el QR estable.</li>
          </ul>
        </div>

        {error && (
          <div className="scan-card" style={{ marginTop: 16 }}>
            <div className="scan-title">Aviso</div>
            <p className="scan-msg">{error}</p>
          </div>
        )}
      </section>

      <section className="scan-result">
        {!entrada && (
          <div className="scan-card">
            <div className="scan-title">Listo para escanear</div>
            <p className="scan-msg">
              Apunta la cámara al QR de la entrada para validar su estado.
            </p>
          </div>
        )}

        {entrada && (
          <div className={`result-card ${esValida ? "valid" : "used"}`}>
            <div className="result-status">
              <div className="icon big">{esValida ? "✅" : "⛔"}</div>
              <div className="status-text">
                {esValida ? "Entrada válida" : `Entrada ${estadoTexto.toLowerCase()}`}
              </div>
            </div>

            <div className="result-body">
              <div className="row">
                <span className="label">Evento</span>
                <span className="value">{entrada.eventoNombre || '-'}</span>
              </div>

              {entrada.zonaNombre && <div className="row"><span className="label">Ubicación actual</span><span className="value">{entrada.zonaNombre}</span></div>}
              <div className="row">
                <span className="label">Estado</span>
                <span className={`badge ${esValida ? "success" : "danger"}`}>
                  {estadoTexto}
                </span>
              </div>
            </div>

            <div className="result-actions">
              <button
                className="btn primary"
                onClick={marcarUsada}
                disabled={!esValida || isMarking}
              >
                {isMarking ? "Marcando..." : "Marcar como usada"}
              </button>

              {/* por si quieres volver manualmente */}
              <button className="btn ghost" onClick={escanearOtra}>
                Escanear otra
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
